import * as React from 'react';

import { maybeParseCalendarDate } from '@rematter/common/dateTime';
import {
  DataGridColumn,
  LocalizedDate,
  Button,
  Menu,
  maybeEmptyInfo,
} from '@rematter/ui-web';
import {
  LucideArrowRight,
  LucideDownload,
  LucideEllipsis,
  LucidePrinter,
} from 'lucide-react';

import { BookingRecord } from '../../../_graphql/queryBookingList';
import { useTranslations } from '../../../i18n/useTranslations';
import { customerPortalRoutes } from '../../../routes';
import { BookingStatusBadge } from '../BookingBadges';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

export type UseBookingsColumnsArgs = {
  onRowClick: (cell: { row: BookingRecord }) => void;
  menuRefs: (id: string) => React.RefObject<HTMLButtonElement | null>;
  onPrint: (bookingId: string) => void;
  onDownload: (bookingId: string) => void;
};

export const useBookingsColumns = ({
  onRowClick,
  menuRefs,
  onPrint,
  onDownload,
}: UseBookingsColumnsArgs): DataGridColumn<BookingRecord>[] => {
  const t = useTranslations();
  const [, navigate] = useModuleLocation();

  return React.useMemo(
    () => [
      {
        key: 'bookingNumber',
        header: t.bookingListView.header.bookingNumber,
        width: 170,
        onClick: onRowClick,
        cell: (cell) => maybeEmptyInfo(cell.row.documentNumber),
      },
      {
        key: 'supplier',
        header: t.bookingListView.header.supplier,
        width: 250,
        onClick: onRowClick,
        cell: (cell) => maybeEmptyInfo(cell.row.supplier.name),
      },
      {
        key: 'purchaseOrderNumber',
        header: t.bookingListView.header.purchaseOrderNumber,
        width: 300,
        onClick: onRowClick,
        cell: (cell) => (
          <div className="flex flex-wrap gap-x-3">
            {!cell.row.purchaseOrders.length && '—'}
            {cell.row.purchaseOrders.map((purchaseOrder) => (
              <span key={purchaseOrder.id}>{purchaseOrder.documentNumber}</span>
            ))}
          </div>
        ),
      },
      {
        key: 'vesselArrivalDate',
        header: t.bookingListView.header.vesselArrivalDate,
        width: 120,
        onClick: onRowClick,
        cell: (cell) => (
          <LocalizedDate
            date={maybeParseCalendarDate(cell.row.vesselArrivalDate)}
          />
        ),
      },
      {
        key: 'vesselName',
        header: t.bookingListView.header.vesselName,
        width: 120,
        onClick: onRowClick,
        cell: (cell) => maybeEmptyInfo(cell.row.vesselName),
      },
      {
        key: 'status',
        header: t.bookingListView.header.status,
        width: 120,
        onClick: onRowClick,
        cell: (cell) => <BookingStatusBadge status={cell.row.status} />,
      },
      {
        key: 'internalNotes',
        header: t.bookingListView.header.internalNotes,
        width: 250,
        onClick: onRowClick,
        cell: (cell) => (
          <div className="max-w-[250px] truncate">
            {maybeEmptyInfo(cell.row.internalNotes)}
          </div>
        ),
      },
      {
        key: 'actions',
        header: t.bookingListView.header.actions,
        width: 80,
        cell: (cell) => (
          <Menu
            placement="bottom end"
            menuClassName="min-w-30"
            trigger={
              <Button
                ref={menuRefs(cell.row.id)}
                variant={'outline'}
                color="neutral"
              >
                <LucideEllipsis size={16} />
              </Button>
            }
            items={[
              {
                label: 'Open Details',
                icon: <LucideArrowRight />,
                onAction: () =>
                  navigate(customerPortalRoutes.bookingDetails, {
                    params: { bookingId: cell.row.id },
                  }),
              },
              'separator',
              {
                label: 'Print Invoices',
                icon: <LucidePrinter />,
                onAction: () => onPrint(cell.row.id),
              },
              {
                label: 'Download Invoices',
                icon: <LucideDownload />,
                onAction: () => onDownload(cell.row.id),
              },
            ]}
          />
        ),
        pinned: 'right',
      },
    ],
    [menuRefs, navigate, onDownload, onPrint, onRowClick, t],
  );
};
